import { useQueryClient } from '@tanstack/react-query';
import { notify } from '@yembo/yemblocks-core';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  JOB_QUERY_KEY,
  ENTITY_KEY,
  IMAGE_QUERY_KEY,
  ITEM_QUERY_KEY,
  LIST_KEY,
  ROOM_QUERY_KEY,
} from 'src/queries/queryKeys';
import {
  commitItemImport,
  deleteItemImport,
  type ImportCandidate,
  type ItemImportProgress,
  isImportSettled,
  type ItemImport,
  jobImportsInProgressQueryKey,
  useItemImportQuery,
} from 'src/queries/ItemImport';

/**
 * One import run end to end: upload, poll while the backend extracts, review,
 * commit.
 *
 * **A spreadsheet's run is a view onto server state, not the state itself.**
 * Once its upload returns, the import lives on the API (reading on the worker,
 * then waiting for review, then — after commit — priced on the worker too),
 * and the job's `ImportProgressBanner` follows it from every job tab. So for a
 * spreadsheet, closing this run (`detach`) only stops *watching*: it never
 * deletes the import, and `resume` picks the same import back up by its key —
 * from the banner's "Review items", in this tab or any other. Photo and list
 * imports keep the older, tab-owned behaviour, where closing abandons them.
 *
 * The upload call is passed in so the three sources (belongings photos, list
 * photos, spreadsheet) share this machinery while each keeps its own endpoint.
 * Nothing enters the job until `commit` is called with the reviewed list —
 * that review step is the whole point of the two-phase design, because a vision
 * pass gets a brand or a year wrong often enough that committing blind would
 * push the correction onto every item afterwards.
 *
 * Instantiated exactly once, by ItemImportProvider, rather than per job —
 * one run at a time, for whichever job's `start` call is currently in
 * flight (see the `jobKey` this returns). That is what lets the run outlive
 * the component that started it: the reviewer can navigate away entirely and
 * the poll keeps going, tracked here instead of in state that would unmount
 * with the page.
 */

/**
 * There is no 'shopping' phase any more: pricing runs on the worker after the
 * commit returns (see `commitItemImport`), and the banner — not this run —
 * shows it. The run is over the moment the items exist.
 */
export type ImportPhase = 'idle' | 'uploading' | 'extracting' | 'ready' | 'committing' | 'failed';

export type ImportSource = 'belongings' | 'lists' | 'spreadsheet';

const GENERIC_FAILURE = 'Something went wrong. Please try again.';

export const useItemImport = ({ onCommitted }: { onCommitted?: () => void } = {}) => {
  const queryClient = useQueryClient();

  const [phase, setPhase] = useState<ImportPhase>('idle');
  const [importKey, setImportKey] = useState<string | null>(null);
  /**
   * Which job the current run belongs to. Set by `start`, not fixed at
   * mount, since one instance of this hook now lives for the whole app
   * (see ItemImportProvider) and runs one import at a time for whichever
   * job started it.
   */
  const [jobKey, setJobKey] = useState<string | null>(null);
  /**
   * Which source (photos/lists/spreadsheet) the current run is. Set
   * alongside `jobKey` by `start` and kept for the run's whole lifetime —
   * unlike the setup-time `pendingImport` (which only needs it up to the
   * moment of upload), the modal still needs to know this while showing
   * extraction progress or the review table.
   */
  const [source, setSource] = useState<ImportSource | null>(null);
  const [candidates, setCandidates] = useState<ImportCandidate[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [processed, setProcessed] = useState(0);
  const [total, setTotal] = useState(0);
  /**
   * Candidates extracted so far. A spreadsheet is always exactly one "file",
   * so `processed`/`total` can only ever read 0 of 1 or 1 of 1 for one — this
   * is the number that actually moves while a large sheet is being read,
   * since the worker publishes results in batches as it goes.
   */
  const [found, setFound] = useState(0);
  /**
   * The API's own progress for the import being watched — rows read of the
   * sheet's total, and an ETA — so the modal's reading step and the banner
   * show the same numbers.
   */
  const [progress, setProgress] = useState<ItemImportProgress | null>(null);
  /**
   * Whether committing should also have the server shop the web for each
   * item's replacement cost. On by default — it is the reason most
   * spreadsheets get uploaded — but every item costs a SerpApi credit, so the
   * review step shows the count and lets it be turned off for a very large
   * sheet.
   */
  const [autoShop, setAutoShop] = useState(true);

  /**
   * Set when the reviewer backs out, so a poll or a commit still in flight
   * stops instead of updating a flow that is gone.
   */
  const isAbandonedRef = useRef(false);
  /** Guards the import record against the cleanup that runs on close. */
  const isCommittedRef = useRef(false);
  /**
   * Identifies which `start()` call is the current one. `commit` reads this
   * before its await and compares again after, so a commit whose reviewer
   * cancelled and immediately started a new import doesn't reset that new
   * run's state out from under it once the old network call finally settles.
   */
  const runIdRef = useRef(0);

  const isPolling = phase === 'extracting' && importKey !== null;
  const { data: itemImport, error: pollError } = useItemImportQuery(importKey, { enabled: isPolling });

  const fail = useCallback((message: string) => {
    if (isAbandonedRef.current) return;
    setError(message);
    setPhase('failed');
  }, []);

  // Drive the phase off whatever the poll last saw. The candidates are seeded
  // once, when extraction settles: after that the reviewer owns them, and
  // re-seeding from a later poll would throw away their edits.
  useEffect(() => {
    if (!isPolling || !itemImport) return;

    setProcessed(itemImport.processedCount ?? 0);
    setTotal(itemImport.fileCount ?? 0);
    setFound(itemImport.resultCount ?? 0);
    setProgress(itemImport.progress ?? null);

    if (itemImport.status === 'failed') {
      fail(itemImport.failureReason ?? 'We could not read that. Please try again.');
      return;
    }

    // Resumed from the banner of a stale page, after someone else (another
    // tab, a colleague) already committed it: reviewing it again would only
    // end in the API's 409.
    if (itemImport.status === 'imported') {
      isCommittedRef.current = true;
      fail('These items have already been added to the job.');
      return;
    }

    if (isImportSettled(itemImport.status)) {
      setCandidates(itemImport.results ?? []);
      setPhase('ready');
    }

    // No client-side timeout any more. It used to give up after ten minutes,
    // but the worker reads a large sheet for longer than that, and with the
    // import owned by the server there is nothing for this tab to give up on:
    // the banner's ETA says how long is left, and the worker marks its own
    // failures.
  }, [isPolling, itemImport, fail]);

  // The query retries a few times on its own; once it gives up the run cannot
  // recover, so surface it rather than spinning forever.
  useEffect(() => {
    if (!isPolling || !pollError) return;
    fail(pollError instanceof Error ? pollError.message : GENERIC_FAILURE);
  }, [isPolling, pollError, fail]);

  /** Puts the run back to nothing, without touching the import on the server. */
  const reset = useCallback(() => {
    setPhase('idle');
    setImportKey(null);
    setJobKey(null);
    setSource(null);
    setCandidates([]);
    setError(null);
    setProcessed(0);
    setTotal(0);
    setFound(0);
    setProgress(null);
  }, []);

  /**
   * Lets the job's import banner pick up an import as soon as it exists,
   * rather than at its next slow poll.
   */
  const refreshBanner = useCallback(
    (forJobKey: string | null) => {
      if (!forJobKey) return;
      void queryClient.invalidateQueries({ queryKey: jobImportsInProgressQueryKey(forJobKey) });
    },
    [queryClient]
  );

  /** Uploads, then hands over to the poll. */
  const start = useCallback(
    async (startJobKey: string, startSource: ImportSource, upload: () => Promise<ItemImport>) => {
      isAbandonedRef.current = false;
      isCommittedRef.current = false;
      runIdRef.current += 1;
      setJobKey(startJobKey);
      setSource(startSource);
      setError(null);
      setCandidates([]);
      setProcessed(0);
      setTotal(0);
      setFound(0);
      setProgress(null);
      setPhase('uploading');

      let created: ItemImport;
      try {
        created = await upload();
      } catch (uploadError) {
        // queryYemboApiCall already surfaced the API's own message.
        if (!isAbandonedRef.current) {
          setError(uploadError instanceof Error ? uploadError.message : GENERIC_FAILURE);
          setPhase('failed');
        }
        return;
      }

      // Even if this tab stopped watching mid-upload, the sheet made it to
      // the server and the worker is reading it — the banner should say so.
      if (startSource === 'spreadsheet') refreshBanner(startJobKey);

      if (isAbandonedRef.current) return;

      setImportKey(created.key);
      setProcessed(created.processedCount ?? 0);
      setTotal(created.fileCount ?? 0);
      setFound(created.resultCount ?? 0);
      setProgress(created.progress ?? null);

      // An import that somehow arrived already extracted skips the poll.
      if (isImportSettled(created.status)) {
        setCandidates(created.results ?? []);
        setPhase(created.status === 'failed' ? 'failed' : 'ready');
        if (created.status === 'failed') setError(created.failureReason ?? GENERIC_FAILURE);
        return;
      }

      setPhase('extracting');
    },
    [refreshBanner]
  );

  /**
   * Picks an existing spreadsheet import back up by its key — the banner's
   * "Review items", which may be in a different tab from the upload, or a
   * day later.
   *
   * It goes through the same poll as a fresh upload ('extracting' with a
   * key): the first read either finds it ready (and seeds the review table
   * from its `results`, which only the single-import read carries), finds
   * it still reading (and keeps polling), or finds it failed or already
   * committed (and says so).
   */
  const resume = useCallback((resumeJobKey: string, resumeImportKey: string) => {
    isAbandonedRef.current = false;
    isCommittedRef.current = false;
    runIdRef.current += 1;
    setJobKey(resumeJobKey);
    setSource('spreadsheet');
    setError(null);
    setCandidates([]);
    setProcessed(0);
    setTotal(0);
    setFound(0);
    setProgress(null);
    setImportKey(resumeImportKey);
    setPhase('extracting');
  }, []);

  /** Creates the reviewed items and refreshes everything they show up in. */
  const commit = useCallback(async () => {
    if (!importKey) return;
    setPhase('committing');

    // Claim the import BEFORE the request: the caller closes the flow as soon
    // as this resolves, and the close cleanup would otherwise delete the very
    // record these items came from. A failed commit leaves an uncommitted
    // import behind, which is harmless — it holds no items.
    isCommittedRef.current = true;
    const myRunId = runIdRef.current;

    try {
      // The server prices whatever it can shop for — a photo, or any words —
      // for photo and list imports as much as spreadsheets. Only spreadsheets
      // show that pass on the banner (the API's in-progress list is
      // spreadsheet-only); a photo import's prices simply arrive with the
      // items' next refresh.
      const { items, skipped, itemImport } = await commitItemImport(importKey, candidates, autoShop);

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [ITEM_QUERY_KEY, LIST_KEY, jobKey] }),
        queryClient.invalidateQueries({ queryKey: [IMAGE_QUERY_KEY, LIST_KEY, jobKey] }),
        queryClient.invalidateQueries({ queryKey: [ROOM_QUERY_KEY, LIST_KEY, jobKey] }),
        queryClient.invalidateQueries({ queryKey: [JOB_QUERY_KEY, ENTITY_KEY, jobKey] }),
      ]);
      // The banner takes over from here when the server is pricing.
      refreshBanner(jobKey);

      const added = items?.length ?? 0;
      const addedLabel = `Added ${added} ${added === 1 ? 'item' : 'items'}`;
      const skippedLabel = skipped ? `, skipped ${skipped} without a name` : '';
      // Only promise pricing when the server actually queued it — a sheet with
      // nothing to search for (no words, no photos) gets none.
      const isPricing = itemImport?.shoppingStatus === 'pending' || itemImport?.shoppingStatus === 'shopping';
      const pricingLabel = isPricing ? '. Searching for comparable items' : '';
      notify.success(`${addedLabel}${skippedLabel}${pricingLabel}`);
      onCommitted?.();

      // Only this run's own success should return it to idle — if the
      // reviewer cancelled and started a new import while this commit was
      // still in flight, runIdRef has since moved on and that new run's
      // state must be left alone.
      if (runIdRef.current === myRunId) reset();
    } catch (commitError) {
      if (runIdRef.current === myRunId) {
        setError(commitError instanceof Error ? commitError.message : GENERIC_FAILURE);
        setPhase('failed');
      }
    }
  }, [importKey, candidates, jobKey, queryClient, onCommitted, autoShop, refreshBanner, reset]);

  /**
   * Stops watching without deleting anything: the server-owned spreadsheet
   * case. The import carries on (or waits for review) and the banner shows
   * it.
   */
  const detach = useCallback(() => {
    isAbandonedRef.current = true;
    refreshBanner(jobKey);
    reset();
  }, [jobKey, refreshBanner, reset]);

  /**
   * Abandons an import the reviewer backed out of, so a package they never
   * committed doesn't linger on the job. Best effort — an uncommitted import
   * holds no items, so a failed cleanup costs nothing.
   */
  const cancel = useCallback(() => {
    const key = importKey;
    const wasCommitted = isCommittedRef.current;
    const cancelledJobKey = jobKey;

    isAbandonedRef.current = true;
    reset();

    // A committed import is the provenance of real items — deleting it would
    // orphan them from the package they came from.
    if (!key || wasCommitted) return;
    deleteItemImport(key)
      .catch(() => {
        /* An uncommitted import holds no items; a failed cleanup is not worth a toast. */
      })
      .finally(() => refreshBanner(cancelledJobKey));
  }, [importKey, jobKey, refreshBanner, reset]);

  return {
    jobKey,
    importKey,
    source,
    phase,
    processed,
    total,
    found,
    progress,
    candidates,
    setCandidates,
    error,
    autoShop,
    setAutoShop,
    start,
    resume,
    commit,
    detach,
    cancel,
  };
};
